import { ThemeProvider } from './lib/themes/ThemeProvider';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, useSearchParams, useNavigate } from 'react-router-dom';
import { queryClient } from './lib/query-client';
import { DeviceServiceProvider, useDeviceService } from './contexts/DeviceServiceContext';
import { useDeviceAuditLogging } from './hooks/useDeviceAuditLogging';
import { SignalRProvider } from './contexts/SignalRContext';
import { useEffect } from 'react';
import { uploadQueueService } from './services/UploadQueueService';
import { useNetworkStatus } from './hooks/useNetworkStatus';
import { useMeasurementWizardStore } from './store/useMeasurementWizardStore';
import { useDeviceStore } from './store/useDeviceStore';
import { useConnectionWizard } from './features/device/hooks/useConnectionWizard';
import { RECOVERY, DEVICE_STORAGE } from './services/device/constants';
import { deviceService } from './services/device';
import logger from './lib/logger';

import { MainLayout } from './components/layout/MainLayout';
import { AnalysisLayout } from './components/layout/AnalysisLayout';
import { ProtectedRoute } from './components/auth';
import { LoginPage } from './features/auth/LoginPage';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { TestsPage } from './features/tests/TestsPage';
import { UsersPage } from './features/users/UsersPage';
import { LabsPage } from './features/labs/LabsPage';
import { NewMeasurement } from './pages/NewMeasurement';
import { DeviceTestPage } from './features/device-test/DeviceTestPage';
import { DevicesPage } from './features/devices/DevicesPage';
import { GuidePage } from './features/guide/GuidePage';
import { ClassificationDiagnosticsPage } from './features/diagnostics/ClassificationDiagnosticsPage';
import { HarnessPage, BatchBrowser, BatchReviewPage } from './features/harness';
import { MeasurementAnalysisPage } from './components/MeasurementAnalysis';
import { features } from './config/features';
import { NotFoundPage } from './components/common/NotFoundPage';

import { ConnectionWizard } from './features/device/ConnectionWizard';
import { ResultsViewPage } from './pages/ResultsViewPage';

// Module-level: runs synchronously before the first React render.
// If a device was previously paired AND no recovery is pending (or recovery is
// baseline-complete, which has no modal to own reconnect), set state to
// 'connecting' immediately so the wizard never sees 'disconnected' during the
// silent reconnect attempt.
// Skip only for 'baseline'/'specimen' phases where MeasurementInterruptedModal
// owns the reconnect and expects to start from 'disconnected'.
{
  const _inFlight = typeof window !== 'undefined'
    ? useMeasurementWizardStore.getState().measurementInFlight
    : null;
  const _needsModalReconnect = _inFlight?.phase === 'baseline' || _inFlight?.phase === 'specimen';
  if (
    typeof window !== 'undefined' &&
    localStorage.getItem(DEVICE_STORAGE.PAIRED_KEY) &&
    !_needsModalReconnect
  ) {
    useDeviceStore.getState().setConnectionState('connecting');
  }
}

/** Bridges CRC-mismatch (and future device-incident) events to the generic AuditLog — always
 *  mounted, independent of which feature page is active or whether a harness Batch is open. */
function DeviceAuditLogBridge() {
  const deviceService = useDeviceService();
  useDeviceAuditLogging(deviceService);
  return null;
}

function AppRoutes() {
  const [searchParams] = useSearchParams();
  const mockMode = searchParams.get('mock') === 'true';
  const navigate = useNavigate();

  // Derived, not stored: reuses the wizard's existing connectionType as the sole signal
  // for which real-device transport DeviceServiceProvider should hand out. Selecting
  // Bluetooth in the Connection Wizard sets connectionType via the same store this reads,
  // so the provider swaps in a BleDeviceService without any new "selected transport" state.
  const connectionType = useConnectionWizard((state) => state.connectionType);
  const transport: 'usb' | 'ble' = connectionType === 'bluetooth' ? 'ble' : 'usb';

  // Initialize upload queue and monitor network status
  useEffect(() => {
    uploadQueueService.initialize().then(() => {
      // Retry any pending uploads on app startup
      uploadQueueService.retryAll();
    });
  }, []);

  // Auto-cleanup: silently discard measurements that are extremely old (> 24 h)
  // and therefore unrecoverable, so they don't show stale UI on next visit.
  useEffect(() => {
    const { measurementInFlight, resetWizard } = useMeasurementWizardStore.getState();
    logger.warn('[RECOVERY:App] Boot-time check — measurementInFlight:', measurementInFlight);
    if (!measurementInFlight) return;
    const age = Date.now() - measurementInFlight.startedAt;
    logger.warn(`[RECOVERY:App] phase=${measurementInFlight.phase} | age=${Math.round(age / 1000)}s | deviceUid=${measurementInFlight.deviceUid}`);
    if (age > RECOVERY.AUTO_CLEANUP_THRESHOLD_MS) {
      logger.warn('[RECOVERY:App] Too old — discarding via resetWizard()');
      resetWizard();
      return;
    }
    // Boot-time redirect: navigate to the interrupted measurement route so the
    // appropriate component mounts and MeasurementInterruptedModal can show.
    const { testId, currentStep } = useMeasurementWizardStore.getState();
    if (measurementInFlight.phase === 'baseline') {
      // Force wizard back to Step 3 — the user may have advanced to step 4+ while
      // the baseline ran in the background, but Step3TakeSpecimen is the only component
      // that mounts MeasurementInterruptedModal for baseline recovery.
      if (currentStep !== 3) {
        logger.warn(`[RECOVERY:App] baseline recovery: currentStep=${currentStep} → forcing to 3 so Step3TakeSpecimen mounts`);
        useMeasurementWizardStore.getState().setCurrentStep(3);
      }
      logger.warn('[RECOVERY:App] Redirecting → /measurement/new (baseline recovery)');
      navigate('/measurement/new');
    } else if (measurementInFlight.phase === 'baseline-complete') {
      // Baseline is done but specimen hasn't started yet — restore the user to Step 4.
      // MeasurementWizard's mount effect will also set step 4, but setting it here
      // ensures the correct step is in the store before the component renders.
      if (currentStep < 4) {
        useMeasurementWizardStore.getState().setCurrentStep(4);
      }
      logger.warn('[RECOVERY:App] Redirecting → /measurement/new (baseline-complete recovery, step 4)');
      navigate('/measurement/new');
    } else if (measurementInFlight.phase === 'incubating') {
      // Incubation timer was running but the device measurement had not yet started —
      // the device is idle. Standard boot silent reconnect handles the device (no
      // MeasurementInterruptedModal). Restore wizard to Step 6 so the incubation
      // timer resumes from the persisted specimenStartedAt.
      if (currentStep < 6) {
        useMeasurementWizardStore.getState().setCurrentStep(6);
      }
      logger.warn('[RECOVERY:App] Redirecting → /measurement/new (incubating recovery, step 6)');
      navigate('/measurement/new');
    } else if (measurementInFlight.phase === 'specimen') {
      // currentStep is 6 or 7 in the store (set by Step5 before setting measurementInFlight).
      // MeasurementWizard mounts, renders the correct step, and useSpecimenMeasurement
      // detects phase='specimen' → sets showRefreshRecovery=true → MeasurementInterruptedModal.
      logger.warn('[RECOVERY:App] Redirecting → /measurement/new (specimen recovery, step', currentStep, ')');
      navigate('/measurement/new');
    } else {
      logger.warn('[RECOVERY:App] No redirect — phase:', measurementInFlight.phase, '| testId:', testId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount — intentional boot-time check

  // Boot-time silent reconnect: if a device was previously paired, try to
  // reconnect silently using the browser's remembered permission grant.
  // Only runs in real (non-mock) mode. If reconnect fails, the store is
  // flagged so the AppBar can show a clear "unavailable" indicator instead
  // of the normal connection wizard.
  useEffect(() => {
    if (mockMode) return;
    if (!localStorage.getItem(DEVICE_STORAGE.PAIRED_KEY)) return;

    // Skip boot reconnect only when MeasurementInterruptedModal will own it.
    // For 'baseline-complete' and 'incubating' there is no modal — reconnect happens here.
    const _bootInFlight = useMeasurementWizardStore.getState().measurementInFlight;
    if (_bootInFlight?.phase === 'baseline' || _bootInFlight?.phase === 'specimen') {
      logger.debug('[Boot] measurementInFlight phase', _bootInFlight.phase, '— skipping boot reconnect, recovery modal will handle it');
      return;
    }

    logger.debug('[Boot] Prior device pairing found — attempting silent reconnect');
    deviceService.tryAutoReconnect().then((success) => {
      if (!success) {
        logger.debug('[Boot] Silent reconnect failed — device unavailable');
        localStorage.removeItem(DEVICE_STORAGE.PAIRED_KEY);
        useDeviceStore.getState().setReconnectAttemptFailed(true);
        useDeviceStore.getState().setConnectionState('disconnected');
      } else {
        logger.debug('[Boot] Silent reconnect succeeded');
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // Monitor network status for auto-retry
  useNetworkStatus();

  return (
    <DeviceServiceProvider mode={mockMode ? 'mock' : 'real'} transport={transport}>
      {/* Connection Wizard - globally available */}
      <ConnectionWizard />
      <DeviceAuditLogBridge />
      
      <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />

            {/* Full-Screen Wizard - Outside MainLayout */}
            <Route 
              path="/measurement/new" 
              element={
                <ProtectedRoute>
                  <NewMeasurement />
                </ProtectedRoute>
              } 
            />
            
            {/* Results View - Full-Screen, supports both live and historical */}
            <Route 
              path="/measurement/results/:testId" 
              element={
                <ProtectedRoute>
                  <ResultsViewPage />
                </ProtectedRoute>
              } 
            />

            {/* Protected Routes with MainLayout */}
            <Route
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<DashboardPage />} />
              <Route path="/tests" element={<TestsPage />} />
              <Route path="/guide" element={<GuidePage />} />
              <Route
                path="/users"
                element={
                  <ProtectedRoute requiredRoles={['admin', 'user-manager', 'supervisor']}>
                    <UsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/labs"
                element={
                  <ProtectedRoute requiredRoles={['admin', 'lab_manager']}>
                    <LabsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/devices"
                element={
                  <ProtectedRoute requiredRoles={['admin', 'supervisor']}>
                    <DevicesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/device-test"
                element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <DeviceTestPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/diagnostics/classification"
                element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <ClassificationDiagnosticsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/harness"
                element={
                  features.enableResearchFeatures ? (
                    <ProtectedRoute requiredRoles={['researcher']}>
                      <HarnessPage />
                    </ProtectedRoute>
                  ) : (
                    <NotFoundPage />
                  )
                }
              />
              <Route
                path="/harness/batches"
                element={
                  features.enableResearchFeatures ? (
                    <ProtectedRoute requiredRoles={['researcher']}>
                      <BatchBrowser />
                    </ProtectedRoute>
                  ) : (
                    <NotFoundPage />
                  )
                }
              />
              <Route
                path="/harness/batches/:batchId"
                element={
                  features.enableResearchFeatures ? (
                    <ProtectedRoute requiredRoles={['researcher']}>
                      <BatchReviewPage />
                    </ProtectedRoute>
                  ) : (
                    <NotFoundPage />
                  )
                }
              />
            </Route>

            {/* Analysis — full-viewport layout, no outer padding/scroll */}
            <Route
              element={
                features.enableResearchFeatures
                  ? (
                    <ProtectedRoute requiredRoles={['admin', 'researcher']}>
                      <AnalysisLayout />
                    </ProtectedRoute>
                  )
                  : <NotFoundPage />
              }
            >
              <Route path="/analysis" element={<MeasurementAnalysisPage />} />
            </Route>

            {/* 404 Route */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </DeviceServiceProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <SignalRProvider>
            <AppRoutes />
          </SignalRProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;

